import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS, BarController, LineController, BarElement, LineElement, PointElement,
  CategoryScale, LinearScale, Tooltip, Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ChevronLeft, ChevronRight, Landmark } from 'lucide-react';
import { chartColors } from '../utils/chartTheme';
import { useLanguage } from '../contexts/LanguageContext';
import { financeAPI } from '../services/api';
import { reportLoadError } from '../utils/notify';
import { toLocalMonth, addMonths, monthBounds, parseLocalDate } from '../utils/dateUtils';

// Bar + line in one chart needs both controllers registered.
ChartJS.register(BarController, LineController, BarElement, LineElement, PointElement,
  CategoryScale, LinearScale, Tooltip, Legend);

const CURRENCIES = ['SRD', 'USD'];
const CURRENCY_KEY = 'dashboard.financeCurrency';

const readCurrency = () => {
  try {
    const saved = localStorage.getItem(CURRENCY_KEY);
    return CURRENCIES.includes(saved) ? saved : 'SRD';
  } catch {
    return 'SRD';
  }
};

// Finance block of the main dashboard: the selected month's profit (approved
// revenues - approved expenses), its revenues and expenses, cash on hand, the
// last 12 months and a 6-month chart, per currency. Only rendered for users
// with finance view permission (see Dashboard.js).
const DashboardFinance = () => {
  const { t, locale } = useLanguage();
  const thisMonth = toLocalMonth();
  const [currency, setCurrency] = useState(readCurrency);
  const [month, setMonth] = useState(thisMonth);
  const [summary, setSummary] = useState(null);
  const [cash, setCash] = useState(null);
  const [trends, setTrends] = useState([]);

  useEffect(() => {
    try { localStorage.setItem(CURRENCY_KEY, currency); } catch { /* per-viewer convenience only */ }
  }, [currency]);

  useEffect(() => {
    const { start, end } = monthBounds(month);
    financeAPI.getSummary({ start_date: start, end_date: end, currency })
      .then((res) => setSummary(res.data.summary))
      .catch((error) => { setSummary(null); reportLoadError(error, t('Error loading finance data')); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, currency]);

  useEffect(() => {
    Promise.all([
      financeAPI.getCashOnHand({ currency }),
      financeAPI.getTrends({ currency, months: 13 })
    ])
      .then(([cashRes, trendsRes]) => { setCash(cashRes.data); setTrends(trendsRes.data.trends || []); })
      .catch((error) => { setCash(null); setTrends([]); reportLoadError(error, t('Error loading finance data')); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  const money = (n) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(n) || 0);
  const monthLabel = (ym, opts = { month: 'long', year: 'numeric' }) => parseLocalDate(`${ym}-01`).toLocaleDateString(locale, opts);

  // Last 12 months ending this month, newest first; months without records are zeros.
  const byMonth = Object.fromEntries(trends.map((r) => [r.month, r]));
  const last12 = Array.from({ length: 12 }, (_, i) => addMonths(thisMonth, -i)).map((ym) => {
    const r = byMonth[ym] || {};
    const revenues = Number(r.revenues) || 0;
    const expenses = Number(r.expenses) || 0;
    return { ym, revenues, expenses, profit: revenues - expenses };
  });
  const last6 = last12.slice(0, 6).reverse();

  const profit = summary ? summary.total_revenues - summary.total_expenses : null;

  // Revenues in the brand green, expenses in steel, profit as a dark line.
  const colors = chartColors();
  const chartData = {
    labels: last6.map((r) => monthLabel(r.ym, { month: 'short', year: '2-digit' })),
    datasets: [
      { type: 'bar', label: t('Revenues'), data: last6.map((r) => r.revenues), backgroundColor: colors.primary, borderRadius: 3, maxBarThickness: 28 },
      { type: 'bar', label: t('Expenses'), data: last6.map((r) => r.expenses), backgroundColor: colors.steel, borderRadius: 3, maxBarThickness: 28 },
      { type: 'line', label: t('Profit'), data: last6.map((r) => r.profit), borderColor: colors.dark, backgroundColor: colors.dark, borderWidth: 2, tension: 0.2 }
    ]
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: { x: { grid: { display: false } }, y: { grid: { color: colors.grid } } }
  };

  return (
    <section className="finance-block card">
      <div className="card__header">
        <h2 className="card__title"><Landmark aria-hidden="true" />{t('Finance')}</h2>
        <div className="card__actions">
        <div className="month-nav">
          <button type="button" className="btn btn--secondary btn--icon" onClick={() => setMonth(addMonths(month, -1))} aria-label={t('Previous month')}><ChevronLeft aria-hidden="true" /></button>
          <span className="month-label">{monthLabel(month)}</span>
          <button type="button" className="btn btn--secondary btn--icon" onClick={() => setMonth(addMonths(month, 1))} aria-label={t('Next month')}><ChevronRight aria-hidden="true" /></button>
        </div>
        <div className="currency-toggle">
          {CURRENCIES.map((c) => (
            <button key={c} type="button" className={c === currency ? 'active' : ''} onClick={() => setCurrency(c)}>{c}</button>
          ))}
        </div>
        </div>
      </div>

      <div className="finance-figures">
      <div className={`profit-card ${profit === null ? '' : profit >= 0 ? 'positive' : 'negative'}`}>
        <div className="profit-label">{t('Profit this month')}</div>
        <div className="profit-value">{profit === null ? '—' : money(profit)}</div>
        <div className="profit-note">{t('Approved records only')}</div>
      </div>

        <div className="finance-card">
          <div className="finance-card-label">{t('Revenue this month')}</div>
          <div className="finance-card-value">{summary ? money(summary.total_revenues) : '—'}</div>
        </div>
        <div className="finance-card">
          <div className="finance-card-label">{t('Expenses this month')}</div>
          <div className="finance-card-value">{summary ? money(summary.total_expenses) : '—'}</div>
        </div>
        <div className="finance-card">
          <div className="finance-card-label">{t('Cash on Hand')}</div>
          <div className="finance-card-value">{cash ? money(cash.cash_on_hand) : '—'}</div>
        </div>
      </div>

      <div className="finance-details">
        <div className="finance-table-wrap">
          <h3 className="finance-subtitle">{t('Last 12 months')}</h3>
          <table className="finance-table data-table">
            <thead>
              <tr>
                <th>{t('Month')}</th>
                <th className="num">{t('Revenues')}</th>
                <th className="num">{t('Expenses')}</th>
                <th className="num">{t('Profit')}</th>
              </tr>
            </thead>
            <tbody>
              {last12.map((r) => (
                <tr key={r.ym} className={r.ym === month ? 'selected' : ''}>
                  <td>{monthLabel(r.ym, { month: 'short', year: 'numeric' })}</td>
                  <td className="num">{money(r.revenues)}</td>
                  <td className="num">{money(r.expenses)}</td>
                  <td className={`num ${r.profit >= 0 ? 'positive' : 'negative'}`}>{money(r.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="finance-chart">
          <h3 className="finance-subtitle">{t('Revenue vs expenses (6 months)')}</h3>
          <div className="chart-container">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardFinance;
